import { useState, useEffect, useRef } from "react";
import { Mail, ShieldCheck, Loader2, AlertCircle, ChevronDown, ChevronUp } from "lucide-react";
import { TextInput } from "./formFields";
import StatusCheckForm from "./StatusCheck";

const API = process.env.REACT_APP_BACKEND_URL;

export default function EmailGateStep({ email, setEmail, onVerified }) {
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const [blocked, setBlocked] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [showStatusCheck, setShowStatusCheck] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    timerRef.current = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timerRef.current);
  }, [cooldown]);

  const sendOtp = async () => {
    setError("");
    setBlocked(false);
    if (!email || !email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/api/request/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (res.status === 403) {
        setBlocked(true);
        setError(data.detail);
      } else if (!res.ok) {
        setError(data.detail || "Something went wrong. Please try again.");
      } else {
        setOtpSent(true);
        setCooldown(60);
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const verifyOtp = async () => {
    setError("");
    if (!otp || otp.trim().length !== 6) {
      setError("Enter the 6-digit code sent to your email.");
      return;
    }
    setVerifying(true);
    try {
      const res = await fetch(`${API}/api/request/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp: otp.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || "Invalid code. Please try again.");
      } else {
        onVerified(data.token);
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="max-w-md mx-auto" data-testid="request-email-gate">
      <div className="text-center mb-8">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5"
          style={{ backgroundColor: "#194449" }}
        >
          <ShieldCheck className="text-white" size={24} />
        </div>
        <h2 className="text-2xl font-semibold text-gray-900 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
          Verify your Microsoft email
        </h2>
        <p className="text-gray-500 text-sm">
          This intake form is available exclusively to Microsoft (@microsoft.com) requesters. Enter your work email to receive a one-time verification code.
        </p>
      </div>

      {blocked && (
        <div
          className="rounded-2xl border p-5 flex items-start gap-3 mb-5"
          style={{ borderColor: "#F0CFC4", backgroundColor: "#FDF3EF" }}
          data-testid="request-domain-blocked"
        >
          <AlertCircle size={18} className="shrink-0 mt-0.5" style={{ color: "#B5453D" }} />
          <p className="text-sm" style={{ color: "#8A3F35" }}>{error}</p>
        </div>
      )}

      {!otpSent && (
        <>
          <div className="relative mb-2">
            <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setBlocked(false);
                setError("");
              }}
              placeholder="you@microsoft.com"
              data-testid="request-email-input"
              onKeyDown={(e) => e.key === "Enter" && sendOtp()}
              className="w-full pl-11 pr-4 py-3 rounded-xl border text-sm text-gray-800 bg-white focus:outline-none focus:ring-2"
              style={{ borderColor: "#DCE3E1", "--tw-ring-color": "#1D5C56" }}
            />
          </div>
          {!blocked && error && <p className="text-sm mb-4" style={{ color: "#B5453D" }} data-testid="request-email-error">{error}</p>}
          <button
            onClick={sendOtp}
            disabled={sending}
            data-testid="request-send-otp-button"
            className="w-full flex items-center justify-center gap-2 text-white py-3.5 rounded-xl font-semibold transition-colors disabled:opacity-60 mt-3"
            style={{ backgroundColor: "#194449" }}
            onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = "#0B2E38"; }}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#194449")}
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
            {sending ? "Sending code\u2026" : "Send Verification Code"}
          </button>
        </>
      )}

      {otpSent && (
        <div data-testid="request-otp-form">
          <p className="text-sm text-gray-500 mb-4 text-center">
            A 6-digit code was sent to <span className="font-semibold text-gray-800">{email}</span>. It expires in 10 minutes.
          </p>
          <TextInput
            value={otp}
            onChange={setOtp}
            placeholder="000000"
            testId="request-otp-input"
          />
          {error && <p className="text-sm mt-3" style={{ color: "#B5453D" }} data-testid="request-otp-error">{error}</p>}
          <button
            onClick={verifyOtp}
            disabled={verifying}
            data-testid="request-verify-otp-button"
            className="w-full flex items-center justify-center gap-2 text-white py-3.5 rounded-xl font-semibold transition-colors disabled:opacity-60 mt-4"
            style={{ backgroundColor: "#194449" }}
            onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = "#0B2E38"; }}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#194449")}
          >
            {verifying ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
            {verifying ? "Verifying\u2026" : "Verify & Continue"}
          </button>
          <div className="text-center mt-4">
            <button
              onClick={sendOtp}
              disabled={cooldown > 0 || sending}
              data-testid="request-resend-otp-button"
              className="text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:underline"
              style={{ color: "#194449" }}
            >
              {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
            </button>
          </div>
        </div>
      )}

      {!otpSent && (
        <div className="mt-6 pt-5 border-t text-center" style={{ borderColor: "#E2EBE9" }}>
          <button
            onClick={() => setShowStatusCheck((v) => !v)}
            data-testid="request-status-toggle-button"
            className="text-sm font-medium inline-flex items-center gap-1.5 hover:underline"
            style={{ color: "#194449" }}
          >
            Already submitted a request? Check its status
            {showStatusCheck ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {showStatusCheck && (
            <div className="mt-4 text-left">
              <StatusCheckForm />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
